import { safeJsonParse } from './safe-json';
import type { QwenRegion } from './cost-model';

/*
 * The payload types are re-declared here rather than imported: the dashboard
 * bundle cannot reach `src/` (web/tsconfig.json includes only web/src). The
 * repo does this deliberately elsewhere — see `src/services/money-format.ts`'s
 * docstring — and `pricing-contract.test.ts` pins these names against the
 * endpoint's own.
 */

export type SttLeg = 'speaker' | 'participant' | 'mixed';

export interface TokenRates {
  sttInputAudio: number;
  sttOutputText: number;
  ttsInputText: number;
  ttsOutputAudio: number;
}

export interface TokenConversions {
  sttInputAudioPerConnectedHour: number;
  /** Already doubled for the transcript and its translation. */
  sttOutputTextPerSpeechHour: number;
  ttsInputTextPerGeneratedHour: number;
  ttsOutputAudioPerGeneratedHour: number;
}

export interface PublishedShape {
  id: string;
  sttLegs: SttLeg[];
  ttsSource: SttLeg | null;
  /** Every voiced side. Optional only so a hand-built test shape may omit it; the endpoint always sends it. */
  ttsSources?: SttLeg[];
}

/** Soniox's entry (`id: 'soniox'`), the one the wallet cards price. */
export interface ProviderPricing {
  id: string;
  name: string;
  billingModel: 'token';
  tokenRatesUsdPerMillion: TokenRates;
  tokenConversions: TokenConversions;
  shapes: PublishedShape[];
}

/**
 * One region's managed-Qwen entry, mirroring `QwenProviderPricing` in `src/routes/pricing.ts`:
 * other rates, other conversions, and shapes priced per hour. Singapore's is `id: 'kizunaai_qwen'`,
 * in USD alone. Beijing's is `id: 'kizunaai_qwen_cn'`, listed only while a USD/CNY rate is stored,
 * and adds the three CNY fields: `currency`, the CNY rates after K, and the rate (`fx`) the USD
 * figures beside them were divided by, with the day it belongs to.
 */
export interface QwenProviderPricing {
  id: string;
  name: string;
  billingModel: 'token';
  region: QwenRegion;
  /** The day the vendor's list prices behind these figures were read. */
  pricesReadOn: string;
  currency?: 'USD' | 'CNY';
  /** CNY per million tokens, after K. */
  tokenRatesCnyPerMillion?: { audioIn: number; textOut: number; audioOut: number; imageIn: number };
  fx?: { cnyPerUsd: number; date: string };
  tokenRatesUsdPerMillion: { audioIn: number; textOut: number; audioOut: number; imageIn: number };
  tokenConversions: {
    audioInTokensPerSecond: number;
    audioOutTokensPerSecondOfSpeech: number;
    textOutTokensPerSecondOfSpeech: number;
    /** Image tokens one direction's screen context adds per second of speech. */
    imageInTokensPerSecondOfSpeech: number;
  };
  /**
   * One direction's screen-context add-on per hour of speech, after K and rounded: the figure a session's grant is
   * sized from. The bill is the session's `video_tokens` × the list image price × K, not this figure.
   */
  screenContextUsdPerHour: number;
  shapes: { id: 'speaking' | 'text-only'; usdPerHour: number }[];
}

export interface PricingResponse {
  pricesReadOn: string;
  providers: (ProviderPricing | QwenProviderPricing)[];
}

/**
 * The Soniox entry, found by id, or null. The wallet cards and the balance estimate price
 * Soniox's shapes, and the list now carries another provider of another shape, so no consumer
 * may take an entry by its position.
 */
export function sonioxProvider(pricing: PricingResponse | null | undefined): ProviderPricing | null {
  return (pricing?.providers.find((p): p is ProviderPricing => p.id === 'soniox')) ?? null;
}

/** The entry id each region's managed Qwen is published under. */
const QWEN_PROVIDER_IDS: Record<QwenRegion, string> = { sg: 'kizunaai_qwen', cn: 'kizunaai_qwen_cn' };

/**
 * The managed Qwen entry for a region, found by id the same way, or null. Beijing's entry is
 * absent whenever no USD/CNY rate is stored, and Singapore's never stands in for it.
 */
export function qwenProviderFor(
  pricing: PricingResponse | null | undefined,
  region: QwenRegion,
): QwenProviderPricing | null {
  const id = QWEN_PROVIDER_IDS[region];
  return (pricing?.providers.find((p): p is QwenProviderPricing => p.id === id)) ?? null;
}

/**
 * Fetch the published rate table.
 *
 * Returns null rather than throwing or guessing. A rate card absent for a
 * moment beats one showing an invented number — the same rule
 * `wallet-format.ts`'s `formatRateInfo` follows, and for the same reason.
 *
 * No credentials: the endpoint is public, and sending them would make a
 * cacheable response look user-specific.
 *
 * `cache: 'no-cache'` forces revalidation. The endpoint sets `Cache-Control:
 * public, max-age=3600`, so without this a returning user can get the OLD
 * `{pricesReadOn, shapes}` body from the browser's disk cache for up to an
 * hour after a deploy — old enough that `providers` may still be an array,
 * just a stale one. Worse, a pre-this-branch cached body has no `providers`
 * field at all, so the guard below returns null and the card shows "Could
 * not load pricing" with a Retry that would hit the same stale cache.
 * `no-cache` still lets the browser cache the response; it just makes it
 * revalidate with the server first, which is the fix.
 */
export async function fetchPricing(): Promise<PricingResponse | null> {
  try {
    const response = await fetch('/api/pricing', { cache: 'no-cache' });
    if (!response.ok) return null;
    const data = await safeJsonParse<PricingResponse>(response);
    if (!data || !Array.isArray(data.providers) || data.providers.length === 0) return null;
    return data;
  } catch (err) {
    console.error('Error fetching pricing:', err);
    return null;
  }
}
